import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import AlerteJoursFeries from "../../others/AlerteJoursFeries"
import {
	fetchJoursFeries,
	postJoursFeries,
	deleteJourFerie,
} from "../../../store/actions/feriesActions"
import { showToast } from "../../../store/actions/toastActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { useGroups } from "../../../hooks/groups"
import Loader from "../../basics/Loader"

// Sous-onglet « Jours fériés » (GE-11) — alimente la règle J/N de la
// tarification police : un jalon qui tombe un jour listé ici (ou un week-end)
// passe la mission en tarif N. Le calcul ne lit QUE les dates matérialisées.
// Maquette : SvpMobile/docs/mockup-jours-feries-config.html. Le catalogue de
// règles et les présélections cantonales viennent du backend (svp/feries.py).

const ORIGINE_REGLE = 0

const MOIS = [
	"janvier",
	"février",
	"mars",
	"avril",
	"mai",
	"juin",
	"juillet",
	"août",
	"septembre",
	"octobre",
	"novembre",
	"décembre",
]
const JOURS = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"]

const styles = {
	card: {
		background: COLORS.surface,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 14,
		overflow: "hidden",
	},
	head: { padding: "18px 20px 12px" },
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	sub: { fontSize: 13, color: COLORS.textMuted, marginTop: 3, maxWidth: 680 },
	rail: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		flexWrap: "wrap",
		padding: "0 20px 14px",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	railLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: COLORS.textLight,
		marginRight: 4,
	},
	yearPill: (selected) => ({
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		cursor: "pointer",
		border: `1px solid ${selected ? COLORS.accent : COLORS.border}`,
		borderRadius: 99,
		background: selected ? COLORS.accentLight : COLORS.surface,
		color: selected ? COLORS.accent : COLORS.textLight,
		fontWeight: selected ? 600 : 500,
		padding: "5px 13px",
	}),
	count: { fontSize: 11, color: COLORS.textLight },
	layout: { display: "flex", alignItems: "flex-start" },
	rules: {
		width: 320,
		flexShrink: 0,
		padding: "14px 18px 18px",
		borderRight: `1px solid ${COLORS.border}`,
	},
	rulesTitle: { fontSize: 13, fontWeight: 600, color: COLORS.text },
	cantonRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		margin: "10px 0 8px",
		fontSize: 13,
		color: COLORS.textMuted,
	},
	hint: { fontSize: 11.5, color: COLORS.textLight, marginBottom: 10 },
	rule: (on) => ({
		display: "flex",
		alignItems: "baseline",
		gap: 8,
		padding: "4px 0",
		fontSize: 13,
		color: on ? COLORS.text : COLORS.textLight,
		cursor: "pointer",
	}),
	ruleDetail: { fontSize: 11, color: COLORS.textLight, marginLeft: "auto" },
	days: { flex: 1, padding: "14px 18px 18px", minWidth: 0 },
	month: { marginBottom: 14 },
	monthTitle: {
		fontSize: 11.5,
		fontWeight: 700,
		letterSpacing: "0.05em",
		textTransform: "uppercase",
		color: COLORS.textMuted,
		marginBottom: 4,
	},
	day: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "5px 8px",
		borderRadius: 8,
		fontSize: 13,
	},
	dayDate: { width: 74, color: COLORS.text, fontWeight: 500 },
	weekendNote: { fontSize: 11, color: COLORS.textLight },
	chip: (regle) => ({
		marginLeft: "auto",
		fontSize: 10.5,
		fontWeight: 600,
		borderRadius: 99,
		padding: "1px 8px",
		color: regle ? COLORS.accent : COLORS.gold,
		background: regle ? COLORS.accentLight : COLORS.warningLight,
	}),
	empty: {
		padding: "30px 12px",
		textAlign: "center",
		color: COLORS.textLight,
		fontSize: 13,
	},
	addRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginTop: 12,
		paddingTop: 12,
		borderTop: `1px solid ${COLORS.border}`,
		flexWrap: "wrap",
	},
	input: {
		font: "inherit",
		fontSize: 13,
		color: COLORS.text,
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		padding: "6px 9px",
		background: COLORS.surface,
	},
	delBtn: (confirming) => ({
		border: "none",
		background: confirming ? COLORS.danger : "transparent",
		color: confirming ? "#fff" : COLORS.textLight,
		borderRadius: 7,
		fontSize: confirming ? 11.5 : 13,
		fontWeight: confirming ? 600 : 400,
		cursor: "pointer",
		padding: confirming ? "3px 9px" : "1px 7px",
	}),
}

const JoursFeriesV2 = () => {
	const dispatch = useDispatch()
	const { isBoss } = useGroups()
	const { data, fetchFeries } = useSelector((s) => s.feriesInfo)
	const anneeCourante = new Date().getFullYear()
	const [annee, setAnnee] = useState(anneeCourante)
	const [confirming, setConfirming] = useState(null) // id du jour à confirmer
	const [addDate, setAddDate] = useState("")
	const [addLibelle, setAddLibelle] = useState("")

	useEffect(() => {
		dispatch(fetchJoursFeries())
	}, [dispatch])

	if (!data)
		return fetchFeries.loading ? (
			<Loader variant="ring" message="Chargement…" />
		) : null

	const jours = data.jours || []
	// Rail : années présentes + courante + suivante (pour pouvoir générer).
	const annees = [
		...new Set([
			...jours.map((j) => parseInt(j.date.slice(0, 4))),
			anneeCourante,
			anneeCourante + 1,
		]),
	].sort()
	const joursAnnee = jours.filter(
		(j) => parseInt(j.date.slice(0, 4)) === annee,
	)
	const reglesActives = new Set(data.regles_actives || [])

	const sauverReglage = (canton, regles) =>
		dispatch(
			postJoursFeries(
				{
					action: "sauver_reglage",
					canton,
					regles_actives: [...regles],
				},
				null,
			),
		)

	const changerCanton = (canton) => {
		// Présélection cantonale appliquée immédiatement (ajustable ensuite).
		const preset = data.preselections[canton] || []
		sauverReglage(canton, preset)
	}

	const toggleRegle = (cle) => {
		const next = new Set(reglesActives)
		if (next.has(cle)) next.delete(cle)
		else next.add(cle)
		sauverReglage(data.canton, next)
	}

	const generer = () =>
		dispatch(postJoursFeries({ action: "generer", annee }, null)).then(
			(json) => {
				if (json)
					dispatch(
						showToast(
							"success",
							json.crees
								? `${json.crees} jour(s) matérialisé(s) pour ${annee}`
								: `Rien à faire — ${annee} est déjà à jour`,
						),
					)
			},
		)

	const ajouter = () => {
		if (!addDate || !addLibelle.trim()) return
		dispatch(
			postJoursFeries(
				{
					action: "ajouter",
					date: addDate,
					libelle: addLibelle.trim(),
				},
				"Jour chômé ajouté",
			),
		).then((json) => {
			if (json) {
				setAnnee(parseInt(addDate.slice(0, 4)))
				setAddLibelle("")
			}
		})
	}

	// Groupement par mois pour l'affichage.
	const parMois = []
	joursAnnee.forEach((j) => {
		const mois = parseInt(j.date.slice(5, 7)) - 1
		const groupe = parMois.find((g) => g.mois === mois)
		if (groupe) groupe.jours.push(j)
		else parMois.push({ mois, jours: [j] })
	})

	return (
		<div style={styles.card}>
			<div style={styles.head}>
				<div style={styles.title}>Jours fériés & jours chômés</div>
				<div style={styles.sub}>
					Alimente la règle jour/nuit de la tarification police : un
					jalon de mission qui tombe un jour listé ici (ou un week-end
					— toujours nuit) passe la mission en tarif N. Le calcul ne
					lit que cette liste, jamais les règles.
				</div>
				{data && data.annees_manquantes && (
					<AlerteJoursFeries annees={data.annees_manquantes} />
				)}
			</div>

			<div style={styles.rail}>
				<span style={styles.railLabel}>Année</span>
				{annees.map((a) => (
					<button
						key={a}
						style={styles.yearPill(a === annee)}
						onClick={() => {
							setAnnee(a)
							setConfirming(null)
						}}
					>
						{a}
						<span style={styles.count}>
							{
								jours.filter(
									(j) => parseInt(j.date.slice(0, 4)) === a,
								).length
							}{" "}
							j
						</span>
					</button>
				))}
			</div>

			<div style={styles.layout}>
				<aside style={styles.rules}>
					<div style={styles.rulesTitle}>
						Règles récurrentes — Suisse
					</div>
					<div style={styles.cantonRow}>
						<span>Canton</span>
						<select
							style={{ ...styles.input, padding: "4px 8px" }}
							value={data.canton}
							disabled={!isBoss}
							onChange={(e) => changerCanton(e.target.value)}
						>
							{data.cantons.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</select>
					</div>
					<div style={styles.hint}>
						Le canton présélectionne les règles légales — ajustables
						ensuite. « Générer » matérialise les dates manquantes de
						l'année choisie ; les jours déjà présents ou supprimés à
						la main ne sont pas touchés.
					</div>
					{data.catalogue.map((r) => (
						<label
							key={r.cle}
							style={styles.rule(reglesActives.has(r.cle))}
						>
							<input
								type="checkbox"
								checked={reglesActives.has(r.cle)}
								disabled={!isBoss}
								onChange={() => toggleRegle(r.cle)}
							/>
							<span>{r.label}</span>
							<span style={styles.ruleDetail}>{r.detail}</span>
						</label>
					))}
					{isBoss && (
						<button
							style={{ ...gs.btnPrimary, marginTop: 12 }}
							onClick={generer}
						>
							Générer {annee}
						</button>
					)}
				</aside>

				<div style={styles.days}>
					{joursAnnee.length === 0 ? (
						<div style={styles.empty}>
							Aucun jour férié matérialisé pour {annee}.
							<br />
							Cliquer « Générer {annee} » pour appliquer les
							règles récurrentes — sans liste, la règle J/N ne
							voit que les week-ends.
						</div>
					) : (
						parMois.map((groupe) => (
							<div key={groupe.mois} style={styles.month}>
								<div style={styles.monthTitle}>
									{MOIS[groupe.mois]} {annee}
								</div>
								{groupe.jours.map((j) => {
									const d = new Date(`${j.date}T00:00:00`)
									const weekend =
										d.getDay() === 0 || d.getDay() === 6
									return (
										<div key={j.id} style={styles.day}>
											<span style={styles.dayDate}>
												{JOURS[d.getDay()]}{" "}
												{j.date.slice(8, 10)}.
												{j.date.slice(5, 7)}
											</span>
											<span>{j.libelle}</span>
											{weekend && (
												<span
													style={styles.weekendNote}
												>
													(week-end — déjà N)
												</span>
											)}
											<span
												style={styles.chip(
													j.origine === ORIGINE_REGLE,
												)}
											>
												{j.origine === ORIGINE_REGLE
													? "règle"
													: "manuel"}
											</span>
											{isBoss && (
												<button
													style={styles.delBtn(
														confirming === j.id,
													)}
													onClick={() => {
														if (
															confirming === j.id
														) {
															setConfirming(null)
															dispatch(
																deleteJourFerie(
																	j.id,
																),
															)
														} else
															setConfirming(j.id)
													}}
												>
													{confirming === j.id
														? "Confirmer ?"
														: "✕"}
												</button>
											)}
										</div>
									)
								})}
							</div>
						))
					)}
					{isBoss && (
						<div style={styles.addRow}>
							<span
								style={{
									fontSize: 12.5,
									color: COLORS.textMuted,
								}}
							>
								Ajouter un jour chômé manuel
							</span>
							<input
								type="date"
								style={styles.input}
								value={addDate}
								onChange={(e) => setAddDate(e.target.value)}
							/>
							<input
								type="text"
								style={{
									...styles.input,
									flex: 1,
									minWidth: 180,
								}}
								placeholder="Libellé — ex. Pont de Noël, fermeture entreprise"
								value={addLibelle}
								onChange={(e) => setAddLibelle(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") ajouter()
								}}
							/>
							<button style={gs.btnSecondary} onClick={ajouter}>
								+ Ajouter
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default JoursFeriesV2
